import { useState } from 'react';
import { useBriefing } from '../hooks/useBriefing.js';
import PopoutModal from './PopoutModal.jsx';

// Daily Briefing card. Lives at the top of the LeftConsole as the first
// thing the user sees on load. Synthesized, single-paragraph prose summary
// of present sky and space conditions. Calm tone, no urgency styling.
// Refreshes every 10 min via the hook.

const MONO = 'var(--mono)';

const S = {
  wrap: {
    borderBottom: '1px solid var(--hair)',
    padding: '10px 12px 12px',
    flexShrink: 0,
    background: 'linear-gradient(180deg, var(--amber-2) 0%, rgba(10,10,12,0) 100%)',
  },
  label: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-label)',
    letterSpacing: '0.18em', fontFamily: MONO,
    marginBottom: 8, textTransform: 'uppercase', fontWeight: 700,
    borderBottom: '1px solid var(--hair)', paddingBottom: 5,
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  },
  body: {
    color: 'var(--txt)', fontSize: 'var(--ui-body)', lineHeight: 1.55,
    fontFamily: MONO, fontWeight: 400,
  },
  meta: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-small)', fontFamily: MONO,
    letterSpacing: '0.04em', marginTop: 8,
    fontStyle: 'italic',
  },
  loading: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', fontFamily: MONO,
    fontStyle: 'italic',
  },
};

function timeAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

export default function DailyBriefing({ observer }) {
  const { briefing, loading } = useBriefing(observer);
  const [open, setOpen] = useState(false);

  return (
    <div style={S.wrap}>
      <div style={S.label}>
        <span>Tonight</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {briefing && (
            <span style={{ color: 'var(--txt-mute)', fontSize: 'var(--ui-small)', letterSpacing: '0.05em' }}>
              {timeAgo(briefing.generated_at)}
            </span>
          )}
          {briefing && (
            <button onClick={() => setOpen(true)} title="Pop out briefing" aria-label="Pop out tonight briefing"
                    style={{ background: 'transparent', border: 'none', color: 'var(--txt-dim)', cursor: 'pointer', padding: '2px 2px', fontSize: 'var(--ui-body)', lineHeight: 1 }}>
              <i className="ti ti-arrows-diagonal" />
            </button>
          )}
        </span>
      </div>
      {loading && !briefing ? (
        <div style={S.loading}>Synthesizing briefing…</div>
      ) : briefing ? (
        <>
          <div style={S.body}>{briefing.text}</div>
          {briefing.signals_summary && (
            <div style={S.meta}>
              Kp {briefing.signals_summary.kp?.toFixed?.(1) ?? '-'}
              {' · '}
              Bz {briefing.signals_summary.bz != null
                ? (briefing.signals_summary.bz > 0 ? '+' : '') + briefing.signals_summary.bz.toFixed(1) + ' nT'
                : '-'}
              {' · '}
              {briefing.signals_summary.humans_in_space ?? '-'} in space
            </div>
          )}
        </>
      ) : (
        <div style={S.loading}>Briefing unavailable.</div>
      )}
      {open && briefing && (
        <PopoutModal title="Tonight, briefing" onClose={() => setOpen(false)}>
          <div style={{ padding: '14px 0 0', fontFamily: 'var(--mono)', fontSize: 'var(--ui-body)', lineHeight: 1.75, color: 'var(--txt)' }}>
            {briefing.text}
          </div>
          {briefing.signals_summary && (
            <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', fontStyle: 'italic' }}>
              Kp {briefing.signals_summary.kp?.toFixed?.(1) ?? '-'}
              {' · '}
              Bz {briefing.signals_summary.bz != null
                ? (briefing.signals_summary.bz > 0 ? '+' : '') + briefing.signals_summary.bz.toFixed(1) + ' nT'
                : '-'}
              {' · '}
              {briefing.signals_summary.humans_in_space ?? '-'} in space
            </div>
          )}
        </PopoutModal>
      )}
    </div>
  );
}